'use client';

import React, { useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import dayjs from 'dayjs';

import { useTranslations, useLocale } from 'next-intl';
import Image from 'next/image';
import type {
	Course as CourseType,
	Courseversion,
	CoursePicture,
} from '../../../../../payload/payload-types';
import { useLivePreview } from '@payloadcms/live-preview-react';
import { AppPathnames } from '../../../../../config';

import { CourseCard } from '../../../_components/Courses/Card';
import { BreadCrumbs } from '../../../_components/Courses/Breadcrumbs';
import { CourseVersionSelect } from '../../../_components/CourseVersionSelect';
import getLanguageShortSuffix from '../../../_utilities/get-language-short-suffx';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@shadcn/components/ui/dialog';
import Play from '../../../_icons/Play';
import { InfoBoxes, InfoboxBlockWithDescription } from '../../../_blocks/InfoBoxes';
import RichTextLexical from '../../../_components/RichTextLexical';
import { getDiscountReduction, useTrackEvent } from '../../../_providers/TrackEventProvider';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { shouldShowCourseDiscountCardBadge } from '../../../_utilities/should-show-course-card-badge';
import { CardBadge } from '../../../_components/Courses/CardBadge';
import { TranslationsInfoType } from './page';
import { useRouter } from '../../../../../navigation';
import LanguageNotFoundDialog from './language-not-found-dialog';
import { Spacer } from '../../../_blocks/Spacer';
import { CourseCardWaitingList } from '../../../_components/Courses/CardWaitingList';
import { logToPayload } from '@/app/(frontend)/_utilities/log-to-payload';

export const CourseTemplate: React.FC<{
	course: CourseType;
	initialCourseversion?: Courseversion;
	translationsInfo: TranslationsInfoType;
}> = ({ course, initialCourseversion, translationsInfo }) => {
	const t = useTranslations('Course');
	const router = useRouter();
	const { data } = (
		useLivePreview as <T>(args: { serverURL: string; initialData: T }) => { data: T }
	)<CourseType>({
		serverURL: process.env.NEXT_PUBLIC_SERVER_URL || '',
		initialData: course,
	});
	const trackEvent = useTrackEvent();
	const sidebarRef = useRef<HTMLElement>(null);
	const courseContentRef = useRef<HTMLDivElement>(null);
	const coursePicture = course.picture as CoursePicture;

	const getCourseDurationText = (courseversion: Courseversion) => {
		const courseDurationWeeks = Math.round(
			dayjs(courseversion.timeEdit.endDate).diff(
				dayjs(courseversion.timeEdit.startDate),
				'week',
				true,
			),
		);

		let courseDurationString = t('course-duration-weeks-short', { courseDurationWeeks });

		if (courseDurationWeeks < 1) {
			const startDate = dayjs(courseversion.timeEdit.startDate);
			const endDate = dayjs(courseversion.timeEdit.endDate);

			// Calculate the number of days the course touches
			let courseDurationDays = endDate.diff(startDate, 'day') + 1;

			// Ensure the course duration is at least 1 day
			if (courseDurationDays <= 0) {
				courseDurationDays = 1;
			}

			courseDurationString = t('course-duration-days-short', { courseDurationDays });
		} else if (courseDurationWeeks > 6) {
			const startDate = dayjs(courseversion.timeEdit.startDate);
			const endDate = dayjs(courseversion.timeEdit.endDate);

			// Extract start and end month and year
			const startMonth = startDate.month(); // 0-based month index
			const startYear = startDate.year();
			const endMonth = endDate.month(); // 0-based month index
			const endYear = endDate.year();

			// Calculate the number of months touched
			const courseDurationMonths = (endYear - startYear) * 12 + (endMonth - startMonth) + 1;

			courseDurationString = t('course-duration-months-short', { courseDurationMonths });
		}

		return courseDurationString;
	};
	const getCourseLanguageTranslation = (language: Courseversion['language']): string => {
		const translations = {
			onlyEstonian: t('onlyEstonian'),
			onlyEnglish: t('onlyEnglish'),
			estonianAndEnglish: t('estonianAndEnglish'),
			estonianButSomeMaterialsInEnglish: t('estonianButSomeMaterialsInEnglish'),
		};

		return translations[language];
	};

	const updateSidebarPosition = () => {
		if (courseContentRef.current && sidebarRef.current) {
			const courseRect = courseContentRef.current.getBoundingClientRect();
			const sidebarRect = sidebarRef.current.getBoundingClientRect();

			const SIDEBAR_SPACE_X_FROM_COURSE = 80;
			let sidebarStartX = Math.round(
				courseRect.left + courseRect.width + SIDEBAR_SPACE_X_FROM_COURSE,
			);

			if (sidebarStartX + sidebarRect.width > window.innerWidth) {
				sidebarStartX = Math.round(courseRect.left + courseRect.width);
			}

			const COURSE_SIDEBAR_TOP_PX = 164;
			// vertically too small window fix:
			if (window.innerHeight > sidebarRect.height + COURSE_SIDEBAR_TOP_PX) {
				sidebarRef.current.classList.remove('absolute');
				sidebarRef.current.classList.add('fixed');
			} else {
				sidebarRef.current.classList.remove('fixed');
				sidebarRef.current.classList.add('absolute');
			}

			sidebarRef.current.style.left = `${sidebarStartX}px`;
		}
	};
	useEffect(() => {
		if (courseContentRef.current && sidebarRef.current) {
			updateSidebarPosition();
			sidebarRef.current.classList.remove('invisible');
			sidebarRef.current.classList.remove('opacity-0');
			sidebarRef.current.classList.add('transition-opacity');
		}
	}, []);

	useEffect(() => {
		const updateSidebarPositionDebounced = debounce(() => {
			updateSidebarPosition();
		}, 50);

		window.addEventListener('resize', updateSidebarPositionDebounced);
		return () => window.removeEventListener('resize', updateSidebarPositionDebounced);
	}, []);

	const crumbs = [
		{
			pathname: '/courses' as AppPathnames,
			title: t('courses'),
			slug: '',
		},
		{
			pathname: '/courses/[slug]' as AppPathnames,
			slug: data.slug as string,
			title: data.title,
		},
	];

	let isInWaitingList = false;
	let infoBoxesContent: InfoboxBlockWithDescription['infoboxes'] = [];

	// is available for purchase
	if (initialCourseversion && typeof initialCourseversion !== 'string') {
		infoBoxesContent = [
			{
				title: getCourseDurationText(initialCourseversion),
				category: t('date-and-time'),
				description: dayjs(initialCourseversion.timeEdit?.startDate).isSame(
					dayjs(initialCourseversion.timeEdit?.endDate),
					'day',
				)
					? dayjs(initialCourseversion.timeEdit?.startDate).format('DD.MM.YYYY')
					: `${dayjs(initialCourseversion.timeEdit?.startDate).format('DD.MM.YYYY')} - ${dayjs(initialCourseversion.timeEdit?.endDate).format('DD.MM.YYYY')}`,
			},
			{
				title: t('courseversion-mode-short', {
					mode: initialCourseversion.mode,
				}),
				category: t('method'),
				description: t('courseversion-mode-long', {
					mode: initialCourseversion.mode,
				}),
			},
			{
				title: getLanguageShortSuffix(initialCourseversion.language),
				category: t('language'),
				description: getCourseLanguageTranslation(initialCourseversion.language),
			},
			{
				title: course.hasEcts
					? t('ects-count', { ectsCount: course.ects })
					: t('ac-h-count', {
							acHCount:
								(initialCourseversion.classroomMeetingCount ?? 0) +
								(initialCourseversion.individualWorkCount ?? 0),
						}),
				category: t('capacity'),
				description: t.rich('capacity-explanation-text', {
					classroomMeetingCount: initialCourseversion.classroomMeetingCount ?? 0,
					individualWorkCount: initialCourseversion.individualWorkCount ?? 0,
					p: text => <p className="body-small-regular">{text}</p>,
					'classroom-meetings': content => <div className="flex flex-col">{content}</div>,
					'individual-work': content => <div className="flex flex-col">{content}</div>,

					wrapper: content => <div className="flex flex-col space-y-2">{content}</div>,
				}) as string | React.ReactElement,
			},
		];
	} else {
		// is in waitingList
		isInWaitingList = true;
	}

	const courseImage = coursePicture.sizes?.card?.url || coursePicture.url;

	if (!courseImage) {
		logToPayload('CourseImage missing: ', {
			coursePicture,
			courseId: course.id,
			courseversionId: initialCourseversion?.id,
		});
	}

	return (
		<React.Fragment>
			<div
				ref={courseContentRef}
				className="lg:mr-course-sidebar-space flex w-full flex-col px-4 pb-10 lg:max-w-[586px] lg:flex-1 lg:px-6 lg:pb-[120px] xl:max-w-[800px]"
			>
				<Spacer size="80" />
				<BreadCrumbs className="mb-10 lg:mb-14" crumbs={crumbs} />
				<div className="mb-8 flex flex-col space-y-8 lg:mb-14">
					<div>
						<h1 className="text-muted-navy mb-4 text-4xl font-bold lg:text-6xl">
							{data.title}
						</h1>
						<p className="text-gold-light text-sm">{t(data.discipline)}</p>
					</div>
					<div className="relative max-w-[400px] lg:hidden">
						{courseImage && (
							<Image
								alt={coursePicture.alt}
								className="rounded-3xl"
								height={200}
								src={courseImage}
								width={400}
								style={{ objectFit: 'cover', height: '200px' }}
							/>
						)}

						{initialCourseversion &&
							shouldShowCourseDiscountCardBadge({
								courseversion: initialCourseversion,
							}) && (
								<CardBadge
									title={initialCourseversion.discountName!}
									subTitle={`${t('until')} ${dayjs(initialCourseversion.discountDeadline).format('DD.MM.YYYY')}`}
									color="red"
								/>
							)}

						{course.videoUrl && (
							<Dialog>
								<DialogTrigger
									onClick={() =>
										trackEvent('course_video_clicked', {
											course_name: course.title,
											component: 'mobile_side_card',
											course_discount_value_eur:
												initialCourseversion &&
												typeof initialCourseversion !== 'string'
													? getDiscountReduction(initialCourseversion)
													: null,
										})
									}
									className="group absolute inset-0 flex cursor-pointer items-center justify-center rounded-3xl bg-[#041E413D]/25"
								>
									<div className="flex h-full flex-col items-center justify-center space-y-4">
										<Play />

										<p className="text-sm leading-6 font-medium text-white group-hover:underline">
											{t('explore-course-content')}
										</p>
									</div>
								</DialogTrigger>
								<DialogContent className="h-full max-w-screen px-0">
									<VisuallyHidden>
										<DialogTitle>Video</DialogTitle>
									</VisuallyHidden>
									<VisuallyHidden>
										<DialogTitle>{t('video-description')}</DialogTitle>
									</VisuallyHidden>
									<iframe
										width="100%"
										height="100%"
										src={`${course.videoUrl}&autoplay=1`}
										title="YouTube video player"
										allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
										referrerPolicy="strict-origin-when-cross-origin"
										allowFullScreen
										style={{ borderRadius: 24 }}
									></iframe>
								</DialogContent>
							</Dialog>
						)}
					</div>
					<RichTextLexical content={data.descriptionRichTextLexical} />

					<CourseVersionSelect />
				</div>

				<InfoBoxes appearance="ghost" infoboxes={infoBoxesContent} />
			</div>

			<aside
				ref={sidebarRef}
				className={`top-course-sidebar w-course-sidebar invisible fixed opacity-0`}
			>
				{initialCourseversion ? (
					<CourseCard course={course} initialCourseversion={initialCourseversion} />
				) : (
					<CourseCardWaitingList course={course} />
				)}
			</aside>
			<Dialog
				open={translationsInfo.isTranslationMissing}
				onOpenChange={() => router.push('/courses')}
			>
				<LanguageNotFoundDialog course={course} translationsInfo={translationsInfo} />
			</Dialog>
		</React.Fragment>
	);
};
